import { Users } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent, Skeleton } from '@/components/ui'
import { useTeamWorkload } from '@/hooks/useTeamWorkload'
import { cn } from '@/lib/utils'

const capacityColors: Record<string, string> = {
  available: 'bg-emerald-500/20 text-emerald-600 dark:text-emerald-400',
  busy: 'bg-amber-500/20 text-amber-600 dark:text-amber-400',
  overloaded: 'bg-red-500/20 text-red-600 dark:text-red-400',
}

const barColors: Record<string, string> = {
  available: 'bg-gradient-to-r from-emerald-500/80 to-emerald-400',
  busy: 'bg-gradient-to-r from-amber-500/80 to-amber-400',
  overloaded: 'bg-gradient-to-r from-red-500/80 to-red-400',
}

export function TeamWorkload() {
  const { data, isLoading } = useTeamWorkload()

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Users className="h-4 w-4" />
            Team Workload
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Skeleton key={i} className="h-[32px] rounded-lg" />
          ))}
        </CardContent>
      </Card>
    )
  }

  const members = data?.members ?? []
  const maxTasks = Math.max(...members.map((m) => m.active_tasks), 1)

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <Users className="h-4 w-4" />
          Team Workload
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {members.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No team members found
          </p>
        ) : (
          <>
            {members.map((member) => {
              const barWidth = maxTasks > 0 ? (member.active_tasks / maxTasks) * 100 : 0

              return (
                <div key={member.user_id} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium truncate max-w-[120px]">{member.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">{member.active_tasks}</span>
                      <span
                        className={cn(
                          'text-[10px] font-semibold px-1.5 py-0.5 rounded-full capitalize',
                          capacityColors[member.capacity] || capacityColors.available,
                        )}
                      >
                        {member.capacity}
                      </span>
                    </div>
                  </div>
                  <div className="h-2 rounded-full bg-muted/50 overflow-hidden">
                    <div
                      className={cn(
                        'h-full rounded-full transition-all duration-500',
                        barColors[member.capacity] || barColors.available,
                      )}
                      style={{ width: `${barWidth}%` }}
                    />
                  </div>
                </div>
              )
            })}

            {(data?.unassigned_count ?? 0) > 0 && (
              <div className="pt-2 border-t border-border">
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  {data!.unassigned_count} unassigned task{data!.unassigned_count > 1 ? 's' : ''}
                </p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
